import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Loader2, MessageSquareText } from "lucide-react";
import {
  useDoctorMessageThreads,
  useDoctorMessageThreadCount,
  type DoctorMessageThreadRow,
} from "@/hooks/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MaskedText } from "@/components/masked-text";
import { ReadState } from "@/components/doctor-messages/read-state";
import { cn } from "@/lib/cn";

type StatusFilter = "open" | "closed" | "all";

const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "open", label: "Åpne" },
  { key: "closed", label: "Lukket" },
  { key: "all", label: "Alle" },
];

function timeAgo(dateStr: string): string {
  const minutes = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60_000);
  if (minutes < 60) return `${minutes}m siden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}t siden`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d siden`;
  return `${Math.floor(days / 30)}mnd siden`;
}

function formatVisitDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("nb-NO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/**
 * "Pasientdialog" tab on kundebehandling: every doctor-chat thread, open ones
 * first, so doctors see at a glance which customers are waiting for an
 * answer. The list carries no message text: clicking a row opens the
 * customer's anamnesis page, through the impersonation reason dialog, focused
 * on the dialog section.
 */
export function DoctorMessagesTab() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<StatusFilter>("open");
  const [search, setSearch] = useState("");

  const { data: threads, isLoading, error } = useDoctorMessageThreads();
  const { data: openCount = 0 } = useDoctorMessageThreadCount({
    status: "open",
  });

  const rows = useMemo(() => {
    let list = threads ?? [];
    if (filter !== "all") list = list.filter((t) => t.status === filter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((t) => {
        const name = `${t.customer.firstName} ${t.customer.lastName}`.toLowerCase();
        const email = (t.customer.email ?? "").toLowerCase();
        return name.includes(q) || email.includes(q);
      });
    }
    return list;
  }, [threads, filter, search]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-admin-danger-400">
        Kunne ikke hente dialogene
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-md border border-admin-card-border p-0.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                "px-3 py-1 text-xs rounded transition-colors",
                filter === f.key
                  ? "bg-admin-primary text-admin-primary-fg"
                  : "text-admin-text-secondary hover:text-admin-text-primary",
              )}
            >
              {f.label}
              {f.key === "open" && openCount > 0 ? ` (${openCount})` : ""}
            </button>
          ))}
        </div>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Søk på kunde…"
          className="max-w-xs"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-admin-card-border">
                <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                  Kunde
                </th>
                <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                  Siste melding
                </th>
                <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                  Laboratoriebesøk
                </th>
                <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                  Sist aktiv
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <ThreadRow
                  key={t.id}
                  thread={t}
                  onOpen={() =>
                    navigate(`/anamnesis/${t.authUserId}?focus=meldinger`)
                  }
                />
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="p-8 text-center text-admin-text-subtle"
                  >
                    {filter === "open"
                      ? "Ingen åpne dialoger"
                      : "Ingen dialoger"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function ThreadRow({
  thread,
  onOpen,
}: {
  thread: DoctorMessageThreadRow;
  onOpen: () => void;
}) {
  const last = thread.lastMessage;
  const awaitingReply =
    thread.status === "open" && last?.senderKind === "user";
  return (
    <tr
      onClick={onOpen}
      className="border-b border-admin-card-border last:border-b-0 hover:bg-admin-card/50 cursor-pointer"
    >
      <td className="p-3 align-top">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "h-2 w-2 shrink-0 rounded-full",
              awaitingReply ? "bg-admin-success-400" : "bg-admin-text-subtle/40",
            )}
            title={
              awaitingReply
                ? "Venter på svar"
                : thread.status === "open"
                  ? "Åpen"
                  : "Lukket"
            }
          />
          <div>
            <div className="text-sm text-admin-text-primary">
              <MaskedText
                value={`${thread.customer.firstName} ${thread.customer.lastName}`.trim()}
              />
            </div>
            <div className="text-xs text-admin-text-subtle">
              <MaskedText value={thread.customer.email ?? ""} type="email" />
            </div>
          </div>
        </div>
      </td>
      <td className="p-3 align-top max-w-md">
        {last ? (
          <>
            <div className="flex items-center gap-1.5 text-xs text-admin-text-subtle">
              <MessageSquareText className="h-3 w-3" />
              {last.senderKind === "doctor"
                ? last.senderName ?? "Lege"
                : "Kunde"}
              <span>·</span>
              <span>{thread.messageCount} meldinger</span>
              {last.senderKind === "doctor" && (
                <>
                  <span>·</span>
                  <ReadState
                    hasUnread={thread.hasUnread}
                    userReadAt={thread.userReadAt}
                  />
                </>
              )}
            </div>
          </>
        ) : (
          <span className="text-sm text-admin-text-subtle">–</span>
        )}
      </td>
      <td className="p-3 text-sm text-admin-text-subtle align-top whitespace-nowrap">
        {formatVisitDate(thread.labVisitDate)}
      </td>
      <td className="p-3 text-sm text-admin-text-subtle align-top whitespace-nowrap">
        {timeAgo(thread.updatedAt)}
      </td>
    </tr>
  );
}
